import { atom } from "jotai";

import { isAuthenticatedAtom } from "./auth.atoms";

import { store } from "@/lib/store";

import scenesService from "@/services/scenes.service";

import { readGoScenesCache, writeGoScenesCache } from "@/lib/goScenesCache";

/**
 * Estado de las go scenes del usuario (diseño elegido + escenas instaladas).
 *
 * ORIGEN DE VERDAD = la DB del backend (`go_user_scenes`). Este atom es una
 * proyección en memoria: se hidrata primero desde la cache IndexedDB (paint
 * instantáneo) y luego se reconcilia con `GET /go/scenes`. `null` = aún no
 * cargado.
 */
export interface GoScenes {
  chosenDesignKey: string | null;

  installedSlugs: string[];
}

export const goScenesAtom = atom<GoScenes | null>(null);

export type GoScenesStatus = "idle" | "loading" | "ready" | "error";

export const goScenesStatusAtom = atom<GoScenesStatus>("idle");

/**
 * `true` cuando el usuario está autenticado y todavía no eligió diseño
 * (no hay fila / `chosenDesignKey == null`). Mientras el estado no cargó
 * (`goScenesAtom === null`) también cuenta como "necesita onboarding": el gate
 * muestra spinner hasta reconciliar, salvo que haya cache.
 */
export const needsOnboardingAtom = atom((get) => {
  if (!get(isAuthenticatedAtom)) return false;

  const gs = get(goScenesAtom);

  return gs === null || gs.chosenDesignKey === null;
});

/**
 * `true` cuando ya hay una respuesta con la que decidir: tenemos valor en
 * memoria/cache (`goScenesAtom !== null`) o el primer intento de carga terminó
 * (`ready`/`error`). Los gates ESPERAN a que esto sea `true` antes de decidir
 * onboarding-vs-app; si no, un usuario que ya eligió diseño se iría un frame a
 * `/onboarding` mientras `loadGoScenes` aún reconcilia (status `idle`→`loading`).
 */
export const goScenesResolvedAtom = atom((get) => {
  if (get(goScenesAtom) !== null) return true;

  const status = get(goScenesStatusAtom);

  return status === "ready" || status === "error";
});

/**
 * Carga las go scenes tras autenticarse:
 *   1. Hidrata el atom desde IndexedDB (paint inmediato, sin red).
 *   2. `GET /go/scenes` para reconciliar contra la DB (origen de verdad).
 *   3. Reescribe la cache IndexedDB con lo reconciliado.
 *
 * Idempotente y seguro de llamar varias veces (login + validación de sesión).
 * Escribe directo en el store jotai global, así puede correr fuera de React.
 */
let inFlight: Promise<void> | null = null;

export async function loadGoScenes(): Promise<void> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    // 1. Hidratar desde cache (paint inmediato) si el atom aún no tiene nada.
    if (store.get(goScenesAtom) === null) {
      const cached = await readGoScenesCache();

      if (cached && store.get(goScenesAtom) === null) {
        store.set(goScenesAtom, {
          chosenDesignKey: cached.chosenDesignKey,
          installedSlugs: cached.installedSlugs,
        });
      }
    }

    store.set(goScenesStatusAtom, "loading");

    // 2. Reconciliar contra la DB.
    try {
      const remote = await scenesService.getGoScenes();

      const next: GoScenes = {
        chosenDesignKey: remote.chosenDesignKey,
        installedSlugs: remote.installedSlugs ?? [],
      };

      store.set(goScenesAtom, next);

      store.set(goScenesStatusAtom, "ready");

      // 3. Refrescar la cache con lo reconciliado.
      void writeGoScenesCache(next);
    } catch {
      // Red caída / backend abajo: nos quedamos con la cache (si la hubo) y
      // marcamos error para que el gate no bloquee con pantalla en blanco.
      store.set(goScenesStatusAtom, "error");
    }
  })();

  try {
    await inFlight;
  } finally {
    inFlight = null;
  }
}

/**
 * Aplica un cambio persistido a DB (elección de diseño / instaladas) al atom +
 * cache, sin re-fetch. Lo usan Onboarding y Live tras un `saveGoScenes` exitoso.
 */
export function applyGoScenes(next: GoScenes): void {
  store.set(goScenesAtom, next);

  store.set(goScenesStatusAtom, "ready");

  void writeGoScenesCache(next);
}
